import axiosServices from 'utils/axios';
import type { AxiosResponse } from 'axios';

/**
 * Legacy shipment API module. Most of what's here has already been split out to
 * `shared/api/shipment.ts` (cross-feature endpoints) and `features/shipments/api.ts`
 * (shipments-only endpoints) during earlier steps of this migration — this file's exports
 * are kept for the 3 callers that still import from here directly (`hooks/useShipmentQuotingCount.js`,
 * `utils/shipment-submission.ts`, `utils/photo-upload.ts`); see docs/PENDING-FINDINGS.md for the
 * full consumer audit.
 */

export const createShipment = (data: unknown): Promise<AxiosResponse<unknown>> => axiosServices.post('/shipments', data);

export const getMyShipments = (): Promise<AxiosResponse<unknown>> => axiosServices.get('/shipments/my-shipments');

export const getAvailableShipments = (filters: unknown): Promise<AxiosResponse<unknown>> =>
  axiosServices.get('/shipments/available', { params: filters });

export const getShipmentById = (id: number | string): Promise<AxiosResponse<unknown>> => axiosServices.get(`/shipments/${id}`);

export const updateShipment = (id: number | string, data: unknown): Promise<AxiosResponse<unknown>> =>
  axiosServices.put(`/shipments/${id}`, data);

export const cancelShipment = (id: number | string, reason: string): Promise<AxiosResponse<unknown>> =>
  axiosServices.patch(`/shipments/${id}/cancel`, { reason });

export const getCancellationPreview = (id: number | string): Promise<AxiosResponse<unknown>> =>
  axiosServices.get(`/shipments/${id}/cancel-preview`);

export const confirmPayment = (id: number | string): Promise<AxiosResponse<unknown>> => axiosServices.post(`/shipments/${id}/confirm-payment`);

export const confirmReceipt = (id: number | string): Promise<AxiosResponse<unknown>> => axiosServices.patch(`/shipments/${id}/confirm-receipt`);

/**
 * Edits a published shipment. Two-step: first call without confirmImpact to check whether
 * the edit affects existing quotes, then again with confirmImpact:true to apply it.
 */
export const editPublishedShipment = (
  id: number | string,
  updates: unknown,
  confirmImpact = false
): Promise<AxiosResponse<unknown>> => axiosServices.patch(`/shipments/${id}/edit`, { updates, confirmImpact });

/**
 * Republishes an expired shipment with new pickup dates. Cancels existing PENDING quotes
 * and puts the shipment back to PENDING status.
 */
export const republishExpiredShipment = (id: number | string, dateData: unknown): Promise<AxiosResponse<unknown>> =>
  axiosServices.patch(`/shipments/${id}/republish`, dateData);

// ── Draft API ──

export const createDraft = (data: unknown): Promise<AxiosResponse<unknown>> => axiosServices.post('/shipments/drafts', { data });

export const updateDraft = (id: number | string, data: unknown): Promise<AxiosResponse<unknown>> =>
  axiosServices.put(`/shipments/drafts/${id}`, { data });

export const listDrafts = (): Promise<AxiosResponse<unknown>> => axiosServices.get('/shipments/drafts');

export const deleteDraft = (id: number | string): Promise<AxiosResponse<unknown>> => axiosServices.delete(`/shipments/drafts/${id}`);

export const publishDraft = (id: number | string): Promise<AxiosResponse<unknown>> => axiosServices.post(`/shipments/drafts/${id}/publish`);

export const getDraftById = (id: number | string): Promise<AxiosResponse<unknown>> => axiosServices.get(`/shipments/drafts/${id}`);

// ── Shipping Options API ──

export const getShippingOptions = (): Promise<AxiosResponse<unknown>> => axiosServices.get('/shipping-options');

// ── Image Uploads ──

export const requestShipmentImageUploadUrl = (data: unknown): Promise<AxiosResponse<unknown>> => axiosServices.post('/shipments/upload-url', data);

// No src/contracts/ schema covers this shape (ShipmentImageSchema describes an image already
// attached to a fetched shipment — a different shape — see contracts/shipment.ts). Minimal
// interface from evidence (AGENTS.md §1): utils/photo-upload.ts destructures both fields off a
// successful upload, and features/shipments/api.ts's independent uploadShipmentImage sibling is
// exercised in tests with this exact shape (imageId: number, storageKey: string).
interface ShipmentImageUploadData {
  imageId: number | string;
  storageKey: string;
}

export const uploadShipmentImage = (
  file: File | Blob,
  { shipmentId, draftId, vehicleIndex }: { shipmentId?: number | string; draftId?: number | string; vehicleIndex?: number }
): Promise<AxiosResponse<ShipmentImageUploadData>> => {
  const formData = new FormData();
  formData.append('photo', file);
  if (shipmentId) formData.append('shipmentId', String(shipmentId));
  if (draftId) formData.append('draftId', String(draftId));
  // Fixed (docs/PENDING-FINDINGS.md finding #78, NEX-1406): `0` is a valid vehicle index, so this
  // checks for presence, not truthiness.
  if (vehicleIndex !== undefined && vehicleIndex !== null) formData.append('vehicleIndex', String(vehicleIndex));

  return axiosServices.post<ShipmentImageUploadData>('/shipments/images/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const confirmShipmentImageUpload = (imageId: number | string): Promise<AxiosResponse<unknown>> =>
  axiosServices.patch(`/shipments/images/${imageId}/confirm`);

export const deleteShipmentImage = (imageId: number | string): Promise<AxiosResponse<unknown>> =>
  axiosServices.delete(`/shipments/images/${imageId}`);

export const getShipmentImageUrl = (imageId: number | string): Promise<AxiosResponse<unknown>> =>
  axiosServices.get(`/shipments/images/${imageId}/url`);

/** Delivers a shipment with receiver data (Driver action). */
export const deliverShipment = (id: number | string, receiverData: unknown): Promise<AxiosResponse<unknown>> =>
  axiosServices.patch(`/shipments/${id}/deliver`, receiverData);

export const createComplaint = (shipmentId: number | string, description: string): Promise<AxiosResponse<unknown>> =>
  axiosServices.post(`/shipments/${shipmentId}/complaints`, { description });
